/**
 * @file errorMessageMapper.ts
 * @description Maps payment errors to user-friendly messages (Task 20.2, Task 33.1)
 * 
 * Provides user-friendly error messages for various payment error types.
 * Supports i18n with EN/PL locales (Task 33.1).
 */

import { getErrorTranslations, SupportedLocale } from './i18n';

export interface PaymentErrorDisplayInfo {
  title: string;
  description: string;
  icon: 'error' | 'warning' | 'info' | 'expired';
  severity: 'critical' | 'error' | 'warning' | 'info';
  isRetryable: boolean;
  requiresNewSession: boolean;
  requiresSupport: boolean;
}

/**
 * Payment error codes from the backend/types
 */
export type PaymentErrorCode =
  | 'ENTRY_FEE_PAYMENT_REQUIRED'
  | 'ENTRY_FEE_PAYMENT_PENDING'
  | 'ENTRY_FEE_PAYMENT_EXPIRED'
  | 'ENTRY_FEE_PAYMENT_MISMATCH'
  | 'PAYMENT_OWNER_MISMATCH'
  | 'NETWORK_ERROR'
  | 'TIMEOUT_ERROR'
  | 'UNKNOWN';

/**
 * Non-text error metadata (icon, severity, flags) - locale-independent
 */
interface ErrorMetadata {
  icon: 'error' | 'warning' | 'info' | 'expired';
  severity: 'critical' | 'error' | 'warning' | 'info';
  isRetryable: boolean;
  requiresNewSession: boolean;
  requiresSupport: boolean;
}

const ERROR_METADATA: Record<PaymentErrorCode, ErrorMetadata> = {
  ENTRY_FEE_PAYMENT_REQUIRED: {
    icon: 'warning',
    severity: 'warning',
    isRetryable: true,
    requiresNewSession: false,
    requiresSupport: false,
  },
  ENTRY_FEE_PAYMENT_PENDING: {
    icon: 'info',
    severity: 'info',
    isRetryable: true,
    requiresNewSession: false,
    requiresSupport: false,
  },
  ENTRY_FEE_PAYMENT_EXPIRED: {
    icon: 'expired',
    severity: 'warning',
    isRetryable: false,
    requiresNewSession: true,
    requiresSupport: false,
  },
  ENTRY_FEE_PAYMENT_MISMATCH: {
    icon: 'error',
    severity: 'error',
    isRetryable: false,
    requiresNewSession: false,
    requiresSupport: true,
  },
  PAYMENT_OWNER_MISMATCH: {
    icon: 'error',
    severity: 'error',
    isRetryable: false,
    requiresNewSession: false,
    requiresSupport: true,
  },
  NETWORK_ERROR: {
    icon: 'warning',
    severity: 'warning',
    isRetryable: true,
    requiresNewSession: false,
    requiresSupport: false,
  },
  TIMEOUT_ERROR: {
    icon: 'warning',
    severity: 'warning',
    isRetryable: true,
    requiresNewSession: false,
    requiresSupport: false,
  },
  UNKNOWN: {
    icon: 'error',
    severity: 'error',
    isRetryable: true,
    requiresNewSession: false,
    requiresSupport: false,
  },
};

/**
 * Build PaymentErrorDisplayInfo by combining locale-specific text with metadata
 */
function buildErrorDisplayInfo(code: PaymentErrorCode, locale: SupportedLocale): PaymentErrorDisplayInfo {
  const translations = getErrorTranslations(locale);
  const text = translations[code];
  const metadata = ERROR_METADATA[code];

  return {
    title: text.title,
    description: text.description,
    ...metadata,
  };
}

/**
 * Extracts error code from an error object
 */
function extractErrorCode(error: Error | null | undefined): PaymentErrorCode {
  if (!error) {
    return 'UNKNOWN';
  }

  // Check for code property (common pattern)
  const errorWithCode = error as Error & { code?: string };
  if (errorWithCode.code) {
    const code = errorWithCode.code.toUpperCase();
    if (code in ERROR_METADATA) {
      return code as PaymentErrorCode;
    }
  }

  // Check error name for network/timeout errors
  const errorName = error.name?.toLowerCase() || '';
  if (errorName.includes('network') || errorName.includes('fetch')) {
    return 'NETWORK_ERROR';
  }
  if (errorName.includes('timeout')) {
    return 'TIMEOUT_ERROR';
  }

  // Check error message for known codes
  const message = error.message?.toUpperCase() || '';
  
  // Check for payment error codes in message
  const paymentCodes: PaymentErrorCode[] = [
    'ENTRY_FEE_PAYMENT_REQUIRED',
    'ENTRY_FEE_PAYMENT_PENDING',
    'ENTRY_FEE_PAYMENT_EXPIRED',
    'ENTRY_FEE_PAYMENT_MISMATCH',
    'PAYMENT_OWNER_MISMATCH',
  ];
  
  for (const code of paymentCodes) {
    if (message.includes(code)) {
      return code;
    }
  }

  // Check for network-related messages
  if (message.includes('NETWORK') || message.includes('FETCH') || message.includes('FAILED TO FETCH')) {
    return 'NETWORK_ERROR';
  }
  if (message.includes('TIMEOUT')) {
    return 'TIMEOUT_ERROR';
  }

  // Check if it's a TypeError (often from fetch failures)
  if (error instanceof TypeError && message.includes('FETCH')) {
    return 'NETWORK_ERROR';
  }

  return 'UNKNOWN';
}

/**
 * Maps a payment error to user-friendly display information
 * 
 * @param error - The error to map (can be Error, null, or undefined)
 * @param locale - The locale for translations (default: 'en')
 * @returns PaymentErrorDisplayInfo with title, description, and action flags
 */
export function getPaymentErrorMessage(
  error: Error | null | undefined,
  locale: SupportedLocale = 'en'
): PaymentErrorDisplayInfo {
  const code = extractErrorCode(error);
  return buildErrorDisplayInfo(code, locale);
}
